import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Alert, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import { State } from '../../redux/state';
import { asPage } from '../../utils/wrappers/asPage';
import { LProps, withLocalization, withRouterAndHelpers, RouterProps } from '../../utils/wrappers';
import { userApiBasePath } from '../../redux/selectors/userApi';
import * as selectors from '../../redux/selectors';
import Footer from '../../shared/Footer';
import AdminGuideLink from '../../shared/AdminGuideLink';
import { formatAdminDateTime, competitionTimeZone } from '../../utils/adminDateTime';
import { makeStoryPath } from '../../utils/routerHelper';

interface UserSummary {
  id: number;
  firstName?: string;
  lastName?: string;
}

interface Nomination {
  id: number;
  uuid: string;
  nomineeUserID: number;
  nominatorUserID: number;
  relatedStoryID?: number;
  nominationText: string;
  divisionEntityID?: number;
  status: string;
  createdAt: string;
  adminNotes?: string;
}

interface Props {
  apiBase: string;
  token: string;
  competitionName: string;
  isCompetitionAdmin: boolean;
  competitionTimezone?: string;
}

interface LocalState {
  items: Nomination[];
  users: Record<number, UserSummary>;
  error: string;
  notice: string;
  statusFilter: string;
  detail: Nomination | null;
}

const STATUS_OPTIONS = [
  { value: 'NEW', label: 'New' },
  { value: 'UNDER_REVIEW', label: 'Under review' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'DECLINED', label: 'Declined' },
  { value: 'FEATURED', label: 'Featured' },
  { value: '', label: 'All' }
];

class HeroNominationsPage extends React.Component<LProps<RouterProps<{}> & Props>, LocalState> {
  public state: LocalState = {
    items: [],
    users: {},
    error: '',
    notice: '',
    statusFilter: 'NEW',
    detail: null
  };

  public componentDidMount() {
    this.load();
  }

  private authHeaders = () => ({
    'Authorization': this.props.token,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  })

  private formatWhen = (iso: string) =>
    formatAdminDateTime(iso, this.props.competitionTimezone)

  private userName = (userID: number) => {
    const user = this.state.users[userID];
    if (!user) {
      return `User #${userID}`;
    }
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return name || `User #${userID}`;
  }

  private load = async () => {
    const { apiBase, competitionName } = this.props;
    const { statusFilter } = this.state;
    const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/hero_nominations${q}`,
        { headers: this.authHeaders() }
      );
      if (res.status === 403) {
        this.setState({ error: 'Moderation access required for Hero Nominations.' });
        return;
      }
      if (!res.ok) {
        this.setState({ error: 'Failed to load hero nominations.' });
        return;
      }
      const data = await res.json();
      this.setState({
        items: data.nominations || data.heroNominations || [],
        users: data.users || {},
        error: ''
      });
    } catch (_err) {
      this.setState({ error: 'Failed to load hero nominations.' });
    }
  }

  private updateStatus = async (id: number, status: string) => {
    const { apiBase, competitionName, t } = this.props;
    const res = await fetch(
      `${apiBase}/competitions/${encodeURIComponent(competitionName)}/hero_nominations/${id}`,
      {
        method: 'PUT',
        headers: this.authHeaders(),
        body: JSON.stringify({ status })
      }
    );
    if (!res.ok) {
      this.setState({ error: `Failed to update nomination ${id}.` });
      return;
    }
    const statusMatch = STATUS_OPTIONS.find((o) => o.value === status);
    const label = statusMatch ? statusMatch.label : status;
    this.setState({
      notice: `${t('Nomination')} ${id} ${t('marked')} ${label}. ${t('Find it under that status filter or All.')}`,
      detail: null
    });
    this.load();
  }

  private renderDetail() {
    const { t, isCompetitionAdmin, history } = this.props;
    const { detail } = this.state;
    if (!detail) {
      return null;
    }
    const storyPath = detail.relatedStoryID
      ? makeStoryPath(detail.relatedStoryID, t)
      : null;
    return (
      <Modal isOpen={true} toggle={() => this.setState({ detail: null })} size="lg">
        <ModalHeader toggle={() => this.setState({ detail: null })}>{t('Nomination detail')}</ModalHeader>
        <ModalBody>
          <dl>
            <dt>{t('Nominee')}</dt>
            <dd>{this.userName(detail.nomineeUserID)}</dd>
            <dt>{t('Nominator')}</dt>
            <dd>{this.userName(detail.nominatorUserID)}</dd>
            <dt>{t('Submitted')}</dt>
            <dd>{this.formatWhen(detail.createdAt)}</dd>
            <dt>{t('Status')}</dt>
            <dd>{detail.status}</dd>
          </dl>
          <h5>{t('Reason')}</h5>
          <p style={{ whiteSpace: 'pre-wrap' }}>{detail.nominationText}</p>
          {detail.adminNotes &&
            <React.Fragment>
              <h5>{t('Admin notes')}</h5>
              <p>{detail.adminNotes}</p>
            </React.Fragment>
          }
        </ModalBody>
        <ModalFooter>
          {storyPath &&
            <Button color="link" onClick={() => history.push(storyPath)}>{t('View story')}</Button>
          }
          {detail.status === 'NEW' &&
            <Button color="secondary" onClick={() => this.updateStatus(detail.id, 'UNDER_REVIEW')}>
              {t('Mark under review')}
            </Button>
          }
          {(detail.status === 'NEW' || detail.status === 'UNDER_REVIEW') &&
            <Button color="success" onClick={() => this.updateStatus(detail.id, 'APPROVED')}>
              {t('Approve')}
            </Button>
          }
          {isCompetitionAdmin && (detail.status === 'APPROVED' || detail.status === 'UNDER_REVIEW') &&
            <Button color="primary" onClick={() => this.updateStatus(detail.id, 'FEATURED')}>
              {t('Feature')}
            </Button>
          }
          <Button color="link" onClick={() => this.updateStatus(detail.id, 'DECLINED')}>
            {t('Decline')}
          </Button>
          <Button color="primary" outline={true} onClick={() => this.setState({ detail: null })}>
            {t('Close')}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  public render() {
    const { t, isCompetitionAdmin } = this.props;
    const { items, error, notice, statusFilter } = this.state;
    return (
      <div>
        <div className="container" style={{ maxWidth: 1100, paddingTop: 24, paddingBottom: 40 }}>
          <h2>{t('Hero Nominations')}</h2>
          <AdminGuideLink sectionId="hero-nominations" label={t('How hero nominations work')} t={t} />
          <p className="cc__lighttext">
            {t('Review Highlight a Hero nominations with full context. Do not feature nominees without human approval.')}
          </p>
          {error && <Alert color="danger">{error}</Alert>}
          {notice && <Alert color="success">{notice}</Alert>}
          <div style={{ marginBottom: 12, maxWidth: 220 }}>
            <Input
              type="select"
              value={statusFilter}
              onChange={(e) => this.setState({ statusFilter: e.target.value }, this.load)}
            >
              {STATUS_OPTIONS.map((opt) =>
                <option key={opt.value || 'all'} value={opt.value}>{t(opt.label)}</option>
              )}
            </Input>
          </div>
          <Table responsive={true} size="sm">
            <thead>
              <tr>
                <th>{t('Nominee')}</th>
                <th>{t('Nominator')}</th>
                <th>{t('Reason')}</th>
                <th>{t('Story')}</th>
                <th>{t('When')}</th>
                <th>{t('Status')}</th>
                <th>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) =>
                <tr key={n.id}>
                  <td>{this.userName(n.nomineeUserID)}</td>
                  <td>{this.userName(n.nominatorUserID)}</td>
                  <td style={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>
                    {n.nominationText.length > 120 ? `${n.nominationText.slice(0, 120)}…` : n.nominationText}
                  </td>
                  <td>
                    {n.relatedStoryID
                      ? <Button size="sm" color="link" onClick={() => this.props.history.push(makeStoryPath(n.relatedStoryID!, t))}>
                        {t('View story')}
                      </Button>
                      : '—'}
                  </td>
                  <td>{this.formatWhen(n.createdAt)}</td>
                  <td>{n.status}</td>
                  <td>
                    <Button size="sm" color="primary" outline={true} onClick={() => this.setState({ detail: n })}>
                      {t('View detail')}
                    </Button>
                    {!isCompetitionAdmin && n.status === 'NEW' &&
                      <Button size="sm" color="secondary" className="ml-1" onClick={() => this.updateStatus(n.id, 'UNDER_REVIEW')}>
                        {t('Review')}
                      </Button>
                    }
                  </td>
                </tr>
              )}
              {items.length === 0 &&
                <tr><td colSpan={7} className="cc__lighttext">{t('No nominations in this status.')}</td></tr>
              }
            </tbody>
          </Table>
        </div>
        {this.renderDetail()}
        <Footer />
      </div>
    );
  }
}

const mapStateToProps = (state: State): Partial<Props> => ({
  apiBase: userApiBasePath(state),
  token: state.token || '',
  competitionName: (state.competition && state.competition.name) || selectors.competitionName(state) || '',
  isCompetitionAdmin: selectors.isCompetitionAdmin(state),
  competitionTimezone: competitionTimeZone(state.competition)
});

export default asPage({ requiresCompetitionStart: false, authenticated: true })(compose(
  withLocalization('account'),
  withRouterAndHelpers,
  connect(mapStateToProps)
)(HeroNominationsPage));
